import { defineMessage, defineMessages } from 'react-intl';
export const automationTitleFallbackMessage = defineMessage({
    defaultMessage: 'Scheduled run',
    id: 'e5ed887b69',
    description: 'Fallback title for an automation row without a name',
});
export const automationDefaultModelMessage = defineMessage({
    defaultMessage: 'Default',
    id: 'f9005bbd91',
    description: 'Label for the default (auto-selected) automation model',
});
export const automationPausedLabelMessage = defineMessage({
    defaultMessage: 'Paused',
    id: '7ff8fd2f23',
    description: 'Status text shown for a paused automation in the automations table',
});
export const automationCompletedLabelMessage = defineMessage({
    defaultMessage: 'Completed',
    id: '6a8058f573',
    description: 'Status text shown for a completed automation',
});
export function canRunAutomationNow(state) {
    const normalizedState = state?.toLowerCase();
    return normalizedState === 'active' || normalizedState === 'paused';
}
export function canResumeAutomation(state) {
    const normalizedState = state?.toLowerCase();
    return normalizedState === 'paused' || normalizedState === 'completed';
}
export const automationManagedByAgentMessage = defineMessage({
    defaultMessage: 'This automation is managed by your agent.',
    id: 'be8593d3aa',
    description: 'Explanation shown when an action is unavailable for an agent-managed automation',
});
// Mirrors AutomationType.is_managed in pplx/common/core/automations/types/shared.py;
// REST exposes type as an untyped string.
const EDIT_RESTRICTED_AUTOMATION_TYPES = new Set(['agent_subscription']);
export function isEditRestrictedType(type) {
    const normalizedType = type?.trim().toLowerCase();
    return normalizedType !== undefined && EDIT_RESTRICTED_AUTOMATION_TYPES.has(normalizedType);
}
// Mirrors the subscription edit allow-list in rest/automations/manage.py: the agent
// owns the definition, but the owner still controls notifications for runs that
// resume their conversation. Worker mail never notifies.
export function canToggleManagedNotifications(type, outputKind) {
    return type?.trim().toLowerCase() === 'agent_subscription' && outputKind === 'existing_thread';
}
export const automationModelUnavailableMessage = defineMessage({
    defaultMessage: 'This model is no longer available. Future runs will use the default model.',
    id: '21f0f33a60',
    description: 'Tooltip explaining that an automation model preference is no longer available',
});
export const automationNotificationsMessage = defineMessage({
    defaultMessage: 'Notifications',
    id: 'c5e0a4eb50',
    description: 'Label for automation notification settings',
});
export const automationNotificationChannelMessages = defineMessages({
    email: {
        defaultMessage: 'Email',
        id: 'd249f520fb',
        description: 'Label for the email notification option in automation notification settings.',
    },
    inApp: {
        defaultMessage: 'In-app',
        id: 'd1c79b445e',
        description: 'Label for the in-app notification option in automation notification settings.',
    },
    push: {
        defaultMessage: 'Push',
        id: '286b93d5ef',
        description: 'Label for the web and mobile push notification option in automation notification settings.',
    },
    mute: {
        defaultMessage: 'No notifications',
        id: '9978af9092',
        description: 'Label for the mute option in automation notification settings that turns off all run notifications.',
    },
    none: {
        defaultMessage: 'None',
        id: 'e3044cbaaa',
        description: 'Value shown when an automation has all notification channels disabled',
    },
    noPreference: {
        defaultMessage: 'No preference',
        id: '5963be74ed',
        description: 'Shown when an automation has no stored notification preference and the agent picks channels per run.',
    },
});
export const automationNotificationPreferenceMessages = defineMessages({
    emailDisabled: {
        defaultMessage: 'Email is disabled in Scheduled tasks settings. <link>Manage notification settings</link>',
        id: '3c15ebbde1',
        description: 'Explains why Email cannot be selected for an automation, with a link to global notification settings.',
    },
    pushDisabled: {
        defaultMessage: 'Push is disabled in Scheduled tasks settings. <link>Manage notification settings</link>',
        id: '3b89525b44',
        description: 'Explains why Push cannot be selected for an automation, with a link to global notification settings.',
    },
    emailAndPushDisabled: {
        defaultMessage: 'Email and Push are disabled in Scheduled tasks settings. <link>Manage notification settings</link>',
        id: '4309a3d672',
        description: 'Explains why Email and Push cannot be selected for an automation, with a link to global notification settings.',
    },
});
export const automationUnreadStatusMessage = defineMessage({
    defaultMessage: 'Unread',
    id: '44e56ef4c0',
    description: 'Accessible label marking an automation run as unread',
});
export const automationRunFailedMessage = defineMessage({
    defaultMessage: 'Run failed',
    id: '38660d1570',
    description: 'Accessible label for an automation run that failed',
});
export const automationBlockedFallbackMessage = defineMessage({
    defaultMessage: 'Requires attention',
    id: 'dee34ba8c4',
    description: 'Status shown on an automation whose latest run is blocked and needs attention',
});
export const automationDeleteConfirmationMessages = defineMessages({
    title: {
        defaultMessage: 'Delete "{title}"?',
        id: '9a9071c1b2',
        description: 'Confirmation title for deleting an automation',
    },
    body: {
        defaultMessage: 'This cannot be undone.',
        id: '7596281087',
        description: 'Confirmation body for deleting an automation',
    },
    bodyWithThreads: {
        defaultMessage: 'Sessions created by its runs will also be deleted. This cannot be undone.',
        id: 'dbbeb0685a',
        description: 'Confirmation body for deleting an automation whose run threads are removed too',
    },
    confirm: {
        defaultMessage: 'Delete',
        id: '6d092c0145',
        description: 'Confirm button label for deleting an automation',
    },
});
//# sourceMappingURL=automationActionMessages.js.map